// ─────────────────────────────────────────────────────────────────────────────
// HrDocumentViewerPage — real routed page for viewing/editing a candidate's
// system-generated HR documents (Offer Letter, Enrolment Form, Induction,
// NDA, Code of Conduct, and future ones like Appointment/Relieving Letter).
//
// Replaces the old fixed-overlay DocumentEditorView: this renders INSIDE the
// persistent AppShell, so the sidebar and topbar stay visible exactly like
// every other module in the app (Notepad, Tasks, etc). No position:fixed
// escape hatch, no "app inside the app" feeling.
//
// Layout mirrors the Notepad pattern: a left panel lists the candidate's
// company documents (click to switch), the right panel shows the selected
// document as a contained "paper" card — the document itself renders inside
// an <iframe> pointed at the existing letterhead-wrapped HTML endpoint, with
// a lightweight ribbon (Bold/Italic/Underline/Print) layered on top since the
// underlying document is contenteditable.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { ROUTES } from '@/routes/paths';
import { COMPANY_DOC_SLUGS } from '../constants';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import {
  ArrowLeft, FileText, CheckCircle, Clock, PenLine, Download, Save, Send, Bold, Italic, Underline,
  Undo2, Redo2, AlignLeft, AlignCenter, AlignRight, Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';

interface CompanyDoc {
  code: string;
  label: string;
  status: string;
  fileUrl?: string;
  generatedAt?: string;
  finalizedAt?: string;
}

interface CandidateForDocs {
  _id: string;
  candidateId: string;
  firstName: string;
  lastName: string;
  companyDocuments: CompanyDoc[];
}

function useCandidateForDocs(id?: string) {
  return useQuery({
    queryKey: ['onboarding-candidates', 'detail', id],
    queryFn: async () => {
      const { data } = await api.get(`/api/hr-onboarding-candidates/candidates/${id}`);
      return data as CandidateForDocs;
    },
    enabled: !!id,
  });
}

export function HrDocumentViewerPage() {
  const { candidateId, docCode } = useParams<{ candidateId: string; docCode: string }>();
  const navigate = useNavigate();
  const { data: candidate, isLoading, refetch } = useCandidateForDocs(candidateId);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  const activeDoc = candidate?.companyDocuments?.find((d) => d.code === docCode);
  const slug = docCode ? COMPANY_DOC_SLUGS[docCode] : undefined;
  const isIssued = activeDoc?.status === 'issued' || activeDoc?.status === 'acknowledged';
  // Every document with a template is viewable AND editable regardless of
  // status — draft, issued, or not yet generated. Editing an issued document
  // and saving reverts it to draft server-side until it's re-issued (see the
  // PUT .../company-documents/:code route); nothing here is ever a one-shot,
  // read-only final copy. This is the "editable until finalized, and still
  // editable + re-issuable after" design the user asked for (2026-08-14),
  // since every employee needs different data in each document.
  const canView = !!slug && !!activeDoc;
  const contentUrl = canView
    ? `/api/hr-onboarding-candidates/candidates/${candidateId}/${slug}?_r=${reloadTick}`
    : null;

  // Reset the "loaded" flag whenever the selected document changes so the
  // spinner reappears while the new iframe document loads.
  useEffect(() => { setReady(false); }, [docCode, reloadTick]);

  function handleIframeLoad() {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    // The template ships its own "Print / Save as PDF" toolbar — hide it so
    // it doesn't duplicate the page-level ribbon above the paper card.
    const internalToolbar = doc.querySelector('.toolbar');
    if (internalToolbar) (internalToolbar as HTMLElement).style.display = 'none';
    setReady(true);
  }

  function exec(command: string, value?: string) {
    const win = iframeRef.current?.contentWindow;
    const doc = iframeRef.current?.contentDocument;
    if (!win || !doc) return;
    win.focus();
    doc.execCommand(command, false, value);
  }

  function getIframeBodyHtml(): string | null {
    const doc = iframeRef.current?.contentDocument;
    const content = doc?.querySelector('.lh-content');
    return content ? content.innerHTML : null;
  }

  // Saves the current on-screen (possibly HR-edited) content as this
  // candidate's draft copy of the document. Safe to call as often as needed —
  // every employee's copy needs different data, so nothing here is locked
  // until HR explicitly approves via "Issue" / "Re-issue" below.
  async function handleSaveDraft(showToast = true): Promise<boolean> {
    const bodyHtml = getIframeBodyHtml();
    if (!bodyHtml || !docCode) {
      if (showToast) toast.error('Document content is not ready yet.');
      return false;
    }
    setSaving(true);
    try {
      await api.put(`/api/hr-onboarding-candidates/candidates/${candidateId}/company-documents/${docCode}`, { bodyHtml });
      await refetch();
      // Bump so the iframe's src (which carries the reload tick as a cache
      // buster) changes and the browser actually re-fetches the just-saved
      // content instead of showing what was already loaded.
      setReloadTick((t) => t + 1);
      if (showToast) toast.success('Draft saved.');
      return true;
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to save draft.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  // Approves the current draft: saves whatever's on screen first (so the
  // issued PDF always matches what HR last saw), then renders it to a real
  // PDF and marks the document issued. Works the same way for a first issue
  // and a re-issue after a later edit.
  async function handleApproveIssue() {
    if (!docCode) return;
    setFinalizing(true);
    try {
      const saved = await handleSaveDraft(false);
      if (!saved) return;
      await api.post(`/api/hr-onboarding-candidates/candidates/${candidateId}/company-documents/${docCode}/finalize`);
      await refetch();
      toast.success(isIssued ? 'Document re-issued.' : 'Document issued.');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to finalize document.');
    } finally {
      setFinalizing(false);
    }
  }

  // Downloads a real, server-rendered PDF instead of relying on the browser's
  // print dialog. This is what makes the "detailed footer only on the true
  // final page" rule possible: the server (Puppeteer) can count actual pages
  // before deciding which footer to place on each one — a browser print
  // preview cannot know that ahead of time. We send the CURRENT (possibly
  // HR-edited) .lh-content markup from the iframe, not the original template,
  // so any on-screen edits are reflected in the downloaded PDF.
  async function handleDownloadPdf() {
    const bodyHtml = getIframeBodyHtml();
    if (!bodyHtml || !docCode) {
      toast.error('Document content is not ready yet.');
      return;
    }
    setDownloading(true);
    try {
      const resp = await api.post(
        '/api/letterhead/pdf',
        { title: activeDoc?.label || 'Document', docType: docCode, bodyHtml },
        { responseType: 'blob' },
      );
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(activeDoc?.label || 'document').replace(/[^a-z0-9-_ ]/gi, '').trim()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  }

  const toolBtn = (label: string, Icon: typeof Bold, action: () => void) => (
    <button
      key={label}
      type="button"
      title={label}
      aria-label={label}
      onMouseDown={(e) => { e.preventDefault(); action(); }}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-on-surface-variant hover:bg-surface-variant hover:text-on-surface active:bg-surface-dim"
    >
      <Icon className="h-4 w-4" />
    </button>
  );

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left: candidate's document checklist */}
      <div className="flex w-72 shrink-0 flex-col overflow-hidden border-r border-outline bg-surface">
        <div className="border-b border-outline p-4">
          <button
            type="button"
            onClick={() => navigate(ROUTES.hrOnboarding)}
            className="inline-flex items-center gap-1.5 text-body-sm text-on-surface-variant hover:text-on-surface"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Onboarding
          </button>
          {candidate && (
            <div className="mt-3">
              <p className="font-semibold text-on-surface">{candidate.firstName} {candidate.lastName}</p>
              <p className="text-body-sm text-on-surface-variant">{candidate.candidateId}</p>
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {isLoading && <div className="p-4"><Spinner /></div>}
          {candidate?.companyDocuments?.map((doc) => {
            const isActive = doc.code === docCode;
            const issued = doc.status === 'issued' || doc.status === 'acknowledged';
            const isDraft = doc.status === 'draft';
            return (
              <button
                key={doc.code}
                type="button"
                onClick={() => navigate(`/hr-onboarding/${candidateId}/documents/${doc.code}`)}
                className={`mb-1 flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-body-sm transition-colors ${
                  isActive ? 'bg-primary-container text-on-primary-container' : 'text-on-surface hover:bg-surface-variant'
                }`}
              >
                {issued ? (
                  <CheckCircle className="h-3.5 w-3.5 shrink-0 text-success-text" />
                ) : isDraft ? (
                  <PenLine className="h-3.5 w-3.5 shrink-0 text-warning-text" />
                ) : (
                  <Clock className="h-3.5 w-3.5 shrink-0 text-on-surface-variant" />
                )}
                <span className="truncate">{doc.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Right: document surface */}
      <div className="flex flex-1 flex-col overflow-hidden bg-surface-container-low">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center"><Spinner /></div>
        ) : !activeDoc ? (
          <div className="flex flex-1 items-center justify-center">
            <EmptyState icon={<FileText className="h-6 w-6" />} title="Select a document" description="Choose a document from the list on the left." />
          </div>
        ) : !canView ? (
          <div className="flex flex-1 items-center justify-center">
            <EmptyState
              icon={<FileText className="h-6 w-6" />}
              title={activeDoc.label}
              description="This document type doesn\u2019t have a generated template yet."
            />
          </div>
        ) : (
          <>
            {/* Ribbon toolbar */}
            <div className="flex shrink-0 items-center gap-1 border-b border-outline bg-surface px-3 py-1.5 overflow-x-auto">
              <span className="mr-2 truncate text-body-sm font-medium text-on-surface">{activeDoc.label}</span>
              <span
                className={`mr-2 shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                  isIssued ? 'bg-success/10 text-success-text' : activeDoc.status === 'draft' ? 'bg-warning/10 text-warning-text' : 'bg-surface-variant text-on-surface-variant'
                }`}
              >
                {isIssued ? 'Issued' : activeDoc.status === 'draft' ? 'Draft' : 'Not generated'}
              </span>
              <Divider />
              {toolBtn('Undo', Undo2, () => exec('undo'))}
              {toolBtn('Redo', Redo2, () => exec('redo'))}
              <Divider />
              {toolBtn('Bold', Bold, () => exec('bold'))}
              {toolBtn('Italic', Italic, () => exec('italic'))}
              {toolBtn('Underline', Underline, () => exec('underline'))}
              <Divider />
              {toolBtn('Align left', AlignLeft, () => exec('justifyLeft'))}
              {toolBtn('Align center', AlignCenter, () => exec('justifyCenter'))}
              {toolBtn('Align right', AlignRight, () => exec('justifyRight'))}
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleSaveDraft(true)}
                  disabled={saving || finalizing}
                  className="inline-flex items-center gap-1.5 rounded-md border border-outline px-3 py-1.5 text-body-sm font-medium text-on-surface hover:bg-surface-variant disabled:opacity-60"
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  {saving ? 'Saving\u2026' : 'Save Draft'}
                </button>
                <button
                  type="button"
                  onClick={handleApproveIssue}
                  disabled={saving || finalizing}
                  className="inline-flex items-center gap-1.5 rounded-md bg-success px-3 py-1.5 text-body-sm font-medium text-white hover:bg-success/90 disabled:opacity-60"
                >
                  {finalizing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  {finalizing ? 'Issuing\u2026' : isIssued ? 'Re-issue' : 'Approve & Issue'}
                </button>
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  disabled={downloading}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-body-sm font-medium text-on-primary hover:bg-primary/90 disabled:opacity-60"
                >
                  {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                  {downloading ? 'Generating\u2026' : 'Download PDF'}
                </button>
              </div>
            </div>

            {/* Paper card */}
            <div className="relative flex-1 overflow-auto p-6">
              {!ready && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Spinner />
                </div>
              )}
              <div className="mx-auto max-w-[880px] overflow-hidden rounded-md border border-outline bg-surface shadow-surface-2">
                <iframe
                  key={contentUrl}
                  ref={iframeRef}
                  src={contentUrl ?? undefined}
                  title={activeDoc.label}
                  onLoad={handleIframeLoad}
                  className="h-[1150px] w-full border-0"
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Divider() {
  return <div className="mx-1 h-5 w-px shrink-0 bg-outline" />;
}
